import axios, { AxiosPromise } from 'axios';

import { IStock } from '../../../@types/model/stock/stock';
import { IStockRelatedData } from '../../../@types/model/stock/stockRelatedData';
import { IStockUpgrade } from '../../../@types/model/stock/stockUpgrade';
import qs from 'qs';
import { IStockViewRelatedData } from '../../../@types/model/stock/stockViewRelatedData';
import { IStockEditFormRelatedData } from '../../../@types/model/stock/StockEditFormRelatedData';
import { IStockSummaryRelatedData } from '../../../@types/model/stock/StockSummaryRelatedData';
import { IStockTomatoReportRelatedData } from '../../../@types/model/stock/stockTomatoReportRelatedData';
import { IStockView } from '../../../@types/model/stock/stockView';
import { IComplianceReplacementStockRelatedData } from '../../../@types/model/compliance/complianceReplacementStockRelatedData';
import { ICombineStockRelatedData } from '../../../@types/model/stock/combineStockRelatedData';
import { IStockAudit } from '../../../@types/model/stock/stockAudit';
import { ICombineStock } from '../../../@types/model/stock/combineStock';
import { IStockAppView } from '../../../@types/model/stock/stockAppView';
import { ISendStockToStorageUnit } from '../../../@types/model/stock/sendStockToStorageUnit';

export default class StockHttpService {
    public static getStockData(siteIds ?: Array<number>) : AxiosPromise<Array<IStock>> {
        return axios.get(`${API_URL}/v1/Stock/GetAllWeb`, {
            params: {
                siteIds,
            },
            paramsSerializer: qs.stringify,
        });
    }

    public static getStockAppView(stockGuid : string) : AxiosPromise<IStockAppView> {
        return axios.get(`${API_URL}/v1/Stock/GetAppStock`, {
            params: {
                stockGuid,
            },
        });
    }

    public static getCombineStockRelatedData(siteIds ?: Array<number>, loadMasterData ?: boolean) : AxiosPromise<ICombineStockRelatedData> {
        return axios.get(`${API_URL}/v1/Stock/GetCombineStockRelatedData`, {
            params: {
                siteIds,
                loadMasterData,
            },
            paramsSerializer: qs.stringify,
        });
    }

    public static getStockEditFormData(siteId : number, stockId ?: number, loadMasterData ?: boolean) : AxiosPromise<IStockEditFormRelatedData> {
        return axios.get(`${API_URL}/v1/Stock/StockFormDataById`, {
            params: {
                siteId,
                stockId,
                loadMasterData,
            },
        });
    }

    public static getStockById(stockId ?: number, onlyIncludeStockLines ?: boolean, onlyIncludeMaterialStockLines ?: boolean, onlyIncludeLines ?: boolean) : AxiosPromise<IStock> {
        return axios.get(`${API_URL}/v1/Stock/StockById`, {
            params: {
                stockId,
                onlyIncludeStockLines,
                onlyIncludeMaterialStockLines,
                onlyIncludeLines,
            },
        });
    }

    public static getStockScreenData(fromDateUnix ?: number, toDateUnix ?: number, siteIds ?: Array<number>, loadMasterData ?: boolean) : AxiosPromise<IStockViewRelatedData> {
        return axios.get(`${API_URL}/v1/Stock/StockWebViewData`, {
            params: {
                fromDateUnix,
                toDateUnix,
                siteIds,
                loadMasterData,
            },
            paramsSerializer: qs.stringify,
        });
    }

    public static getStockTomatoReportRelatedData(fromDateUnix ?: number, toDateUnix ?: number, siteIds ?: Array<number>, loadMasterData ?: boolean) : AxiosPromise<IStockTomatoReportRelatedData> {
        return axios.get(`${API_URL}/v1/Stock/GetTomatoReportData`, {
            params: {
                fromDateUnix,
                toDateUnix,
                siteIds,
                loadMasterData,
            },
            paramsSerializer: qs.stringify,
        });
    }

    public static getStockTransactionData(fromDateUnix ?: number, toDateUnix ?: number, siteIds ?: Array<number>) : AxiosPromise<IStockRelatedData> {
        return axios.get(`${API_URL}/v1/Stock/WebTransactions`, {
            params: {
                fromDateUnix,
                toDateUnix,
                siteIds,
            },
            paramsSerializer: qs.stringify,
        });
    }

    public static getReplacementStock(stockIds : Array<number>) : AxiosPromise<IComplianceReplacementStockRelatedData> {
        return axios.get(`${API_URL}/v1/Stock/GetMatchingReplacementStockDataAsync`, {
            params: {
                stockIds,
            },
            paramsSerializer: qs.stringify,
        });
    }

    public static getDispatchLineReplacementStock(dispatchId : number) : AxiosPromise<Array<IStock>> {
        return axios.get(`${API_URL}/v1/Stock/GetMatchingDispatchLineStock`, {
            params: {
                dispatchId,
            },
        });
    }

    public static getDispatchStocks(dispatchId : number) : AxiosPromise<Array<IStock>> {
        return axios.get(`${API_URL}/v1/Stock/GetDispatchStocks`, {
            params: {
                dispatchId,
            },
        });
    }

    public static getStocks(stockIds : Array<number>) : AxiosPromise<Array<IStock>> {
        return axios.post(`${API_URL}/v1/Stock/GetStockData`, stockIds);
    }

    public static addOrUpdateStock(data : IStock) : AxiosPromise<IStock> {
        return axios.post(`${API_URL}/v1/Stock/PostStock`, data);
    }

    public static mergeStocks(data : ICombineStock) : AxiosPromise<Array<IStockView>> {
        return axios.post(`${API_URL}/v1/Stock/MergeStocks`, data);
    }

    public static combineStockIntoNewStock(data : ICombineStock) : AxiosPromise<Array<IStockView>> {
        return axios.post(`${API_URL}/v1/Stock/CombineStocksIntoNewStock`, data);
    }

    public static upgradeStock(data : IStockUpgrade) : AxiosPromise<IStock> {
        return axios.post(`${API_URL}/v1/Stock/UpgradeStockWeb`, data);
    }

    public static breakDownStock(stockView : IStockView) : AxiosPromise<IStockView> {
        return axios.post(`${API_URL}/v1/Stock/BreakDown`, stockView);
    }

    public static breakDownStockIntoLots(stockView : IStockView) : AxiosPromise<IStockView> {
        return axios.post(`${API_URL}/v1/Stock/BreakDownIntoLots`, stockView);
    }

    public static breakDownMultipleStocksIntoLots(stockIds : Array<number>) : AxiosPromise<Array<IStockView>> {
        return axios.post(`${API_URL}/v1/Stock/BreakDownMultipleStocksIntoLots`, stockIds);
    }

    public static sendStocksToStorageUnit(upsert : ISendStockToStorageUnit) : AxiosPromise<Array<IStock>> {
        return axios.post(`${API_URL}/v1/Stock/SendStocksToStorageUnit`, upsert);
    }

    public static revertStockBreakDown(stockView : IStockView) : AxiosPromise<IStockView> {
        return axios.post(`${API_URL}/v1/Stock/RevertBreakDown`, stockView);
    }

    public static stockDelete = (stockId : number, deleteReason : string) : AxiosPromise<IStock> => {
        return axios.delete(`${API_URL}/v1/Stock/Delete`, {
            params : { stockId, deleteReason },
        });
    };

    public static getStockSummaryData(stockId ?: number, loadMasterData ?: boolean) : AxiosPromise<IStockSummaryRelatedData> {
        return axios.get(`${API_URL}/v1/StockLine/StockSummaryRelatedData`, {
            params: {
                stockId,
                loadMasterData,
            },
        });
    }

    public static getStockAudits(stockId ?: number) : AxiosPromise<Array<IStockAudit>> {
        return axios.get(`${API_URL}/v1/Stock/GetStockAudits`, {
            params: {
                stockId,
            },
        });
    }
}
